import { useEffect, useState, useMemo, useCallback } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import ListPageLayout from "../../components/ListPageLayout";
import SalesListSettingsModal from "../../components/SalesListSettingsModal";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate, useLocation } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllInvoices } from "../../services/SalesInvoiceServices";
import { fetchAndShowInvoicePdf } from "../../utils/invoicePdf";
import moment from 'moment';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import { displayError } from "../../helpers";
import {
    getDateRangeFromSalesSettings,
    getSalesSettingsSummary,
    loadSalesListSettings,
    resolveCustomerFilter,
    resolvePlantFilter,
    saveSalesListSettings,
} from "../../utils/salesListSettings";
import {
    buildInvoiceSummaryStats,
    resolveInvoiceType,
} from '../../utils/invoiceListMetrics';

function SalesInvoiceTable() {
    const InitialData = {
        records: []
    };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const location = useLocation();
    const [cookies] = useCookies(['myToken', 'myUserId']);
    const [isLoading, setIsLoading] = useState(true);
    const [plants, setPlants] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [showSettings, setShowSettings] = useState(false);
    const [salesSettings, setSalesSettings] = useState(() => loadSalesListSettings(cookies.myUserId));
    const [dateOverride, setDateOverride] = useState(null);

    const mapInvoiceList = (invoiceList) => invoiceList.map((invoice) => {
        const salesOrder = invoice.sales_order || {};
        const consignee = salesOrder.consignee || {};
        const customer = consignee.customer || {};
        const salesrep = customer.salesrep || {};
        return {
            plant_alias: invoice.plant?.alias_name || '',
            id: invoice.id,
            invoice_amt: invoice.invoice_amt,
            taxable_amt: invoice.taxable_amt || 0,
            gst_amt: invoice.gst_amt || 0,
            invoice_type: invoice.invoice_type || resolveInvoiceType({
                ...invoice,
                billing_gst_no: invoice.billing_gst_no || '',
            }),
            invoice_no: (invoice.prefix || '') + (invoice.invoice_no || ''),
            invoice_date: invoice.invoice_date,
            invoice_time: invoice.invoice_time,
            doc_type: 'SALES',
            order_no: (salesOrder.prefix || '') + (salesOrder.order_no || ''),
            customer_name: customer.name || '',
            salesrep_name: salesrep.name || '',
            consignee_name: consignee.name || '',
            project_name: consignee.project_name || '',
            pan_no: customer.pan_no || '',
            gst_no: customer.gst_no || '',
            einvoice_irn: invoice.einvoice_irn || '',
            einvoice_ack: invoice.einvoice_ack || '',
            einvoice_status: invoice.einvoice_status,
            billing_gst_no: invoice.billing_gst_no || '',
            einvoice_status_label: Number(invoice.einvoice_status) === 4
                ? 'IRN Cancelled'
                : (invoice.einvoice_irn ? 'Generated' : 'Not Generated'),
            remarks: invoice.remarks,
            status: invoice.status ? 'Active' : 'Cancelled',
            hide_row_edit: invoice.hide_row_edit || (invoice.einvoice_irn ? 'Yes' : 'No'),
            irn_sync_only: invoice.irn_sync_only || false,
        };
    });

    const activeDateRange = useMemo(() => {
        if (dateOverride) {
            return dateOverride;
        }
        return getDateRangeFromSalesSettings(salesSettings);
    }, [dateOverride, salesSettings]);

    const fetchInvoices = useCallback(() => {
        const plantId = resolvePlantFilter(salesSettings, plants);
        const customerIds = resolveCustomerFilter(salesSettings, customers);
        const [rangeFrom, rangeTo] = activeDateRange;

        if (!plantId || !customerIds || !rangeFrom?.isValid() || !rangeTo?.isValid()) {
            setIsLoading(false);
            return Promise.resolve();
        }

        setIsLoading(true);
        return getAllInvoices(cookies, {
            from_date: rangeFrom.format('DD-MM-YYYY'),
            upto_date: rangeTo.format('DD-MM-YYYY'),
            plant_id: plantId,
            customer_ids: customerIds,
        })
            .then((response) => {
                setData({ records: mapInvoiceList(response.invoice_list || []) });
            })
            .catch((error) => {
                console.log(error, 'error');
                displayError(error?.response?.data || { message: 'Failed to load invoices.' }, 'Load Failed');
                setData({ records: [] });
            })
            .finally(() => setIsLoading(false));
    }, [activeDateRange, cookies, customers, plants, salesSettings]);

    const columns = useMemo(
        () => [
            {
                fieldName: 'plant_alias',
                headerName: 'Plant'
            },
            {
                fieldName: "doc_type",
                headerName: 'Doc Type'
            },
            {
                fieldName: 'invoice_type',
                headerName: 'Invoice Type',
            },
            {
                fieldName: "invoice_no",
                headerName: 'Invoice Number'
            },
            {
                fieldName: "invoice_date",
                headerName: 'Invoice Date'
            },
            {
                fieldName: "customer_name",
                headerName: 'Customer Name'
            },
            {
                fieldName: "project_name",
                headerName: 'Site Name'
            },
            {
                fieldName: "invoice_amt",
                headerName: 'Invoice Amount'
            },
            {
                fieldName: 'taxable_amt',
                headerName: 'Taxable Amount',
            },
            {
                fieldName: 'gst_amt',
                headerName: 'Tax Amount',
            },
            {
                fieldName: "einvoice_irn",
                headerName: 'E-Invoice IRN',
            },
            {
                fieldName: "einvoice_ack",
                headerName: 'Ack No',
            },
            {
                fieldName: "einvoice_status_label",
                headerName: 'E-Invoice',
            },
            {
                fieldName: 'salesrep_name',
                headerName: 'Marketting',
            },
            {
                fieldName: "status",
                headerName: 'Status'
            },
            {
                fieldName: "id",
                headerName: "ROW_ACTIONS"
            }
        ],
        [],
    );

    const onEditSalesPosting = (salespostId) => {
        const selected_invoice = data.records.filter(obj => obj.id === salespostId);
        if (selected_invoice[0].remarks === 'sales_invoice') {
            navigate(`/SalesInvoiceEdit/${salespostId}/`);
        } else {
            navigate(`/DirectInvoiceEdit/${salespostId}/`);
        }
    };

    const onPrintSalesPosting = (salespostId, event) => {
        event.preventDefault();
        setIsLoading(true);
        fetchAndShowInvoicePdf(cookies, salespostId, { title: 'Invoice PDF' })
            .finally(() => setIsLoading(false));
    };

    const filteredRecords = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) {
            return data.records;
        }

        const terms = query.split(/\s+/).filter(Boolean);

        return data.records.filter((invoice) => {
            const haystack = Object.values(invoice)
                .filter((value) => value != null && value !== '')
                .map((value) => String(value).toLowerCase())
                .join(' ');

            return terms.every((term) => haystack.includes(term));
        });
    }, [data.records, searchText]);

    const summaryStats = useMemo(
        () => buildInvoiceSummaryStats(filteredRecords),
        [filteredRecords],
    );

    const settingsSummary = useMemo(
        () => getSalesSettingsSummary(salesSettings, plants, customers),
        [salesSettings, plants, customers],
    );

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS'),
            filteredRecords,
            'SalesInvoice',
        );
    };

    const pdfExportColumns = [
        { fieldName: 'invoice_no', headerName: 'Invoice No.' },
        { fieldName: 'invoice_date', headerName: 'Date' },
        { fieldName: 'customer_name', headerName: 'Customer' },
        { fieldName: 'project_name', headerName: 'Site' },
        { fieldName: 'invoice_type', headerName: 'Invoice Type' },
        { fieldName: 'invoice_amt', headerName: 'Invoice Amount' },
        { fieldName: 'taxable_amt', headerName: 'Taxable Amount' },
        { fieldName: 'gst_amt', headerName: 'Tax Amount' },
        { fieldName: 'einvoice_status_label', headerName: 'E Invoice Status' },
        { fieldName: 'salesrep_name', headerName: 'Marketing' },
    ];

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Sales Invoice',
            pdfExportColumns.map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            filteredRecords.map((invoice) => ({
                invoice_no: invoice.invoice_no,
                invoice_date: invoice.invoice_date,
                customer_name: invoice.customer_name,
                project_name: invoice.project_name,
                invoice_type: invoice.invoice_type,
                invoice_amt: invoice.invoice_amt,
                taxable_amt: invoice.taxable_amt,
                gst_amt: invoice.gst_amt,
                einvoice_status_label: invoice.einvoice_status_label || (invoice.einvoice_irn ? 'Generated' : 'Not Generated'),
                salesrep_name: invoice.salesrep_name,
            })),
            'SalesInvoice.pdf',
        );
    };

    const handleSettingsSave = (nextSettings) => {
        saveSalesListSettings(cookies.myUserId, nextSettings);
        setSalesSettings(nextSettings);
        setDateOverride(null);
        setShowSettings(false);
        Swal.fire('Updated!', '', 'success');
    };

    useEffect(() => {
        Promise.all([
            getUserAllowedPlants(cookies),
            getAllCustomerMain(cookies),
        ])
            .then(([plantList, customerList]) => {
                const plantOptions = (plantList.plant_list || [])
                    .filter((plant) => plant.status)
                    .map((plant) => ({
                        value: plant.id,
                        label: plant.plant_alias,
                    }));
                const customerOptions = (customerList.customer_list || [])
                    .filter((customer) => customer.status)
                    .map((customer) => ({
                        value: customer.id,
                        label: customer.name,
                    }));

                setPlants(plantOptions);
                setCustomers(customerOptions);
                setIsInitialized(true);

                if (plantOptions.length === 0) {
                    setIsLoading(false);
                }
            })
            .catch((error) => {
                console.log(error, 'error');
                setIsLoading(false);
            });
    }, [cookies]);

    useEffect(() => {
        if (location.state?.invoiceDate) {
            const invoiceMoment = moment(location.state.invoiceDate, 'DD-MM-YYYY');
            if (invoiceMoment.isValid()) {
                setDateOverride([
                    invoiceMoment.clone().startOf('month'),
                    moment.max(moment(), invoiceMoment).endOf('month'),
                ]);
            }
        }
    }, [location.state?.invoiceDate, location.state?.refreshAt]);

    useEffect(() => {
        if (!isInitialized || plants.length === 0) {
            return;
        }
        fetchInvoices();
    }, [isInitialized, plants.length, customers.length, location.key, salesSettings, activeDateRange, fetchInvoices]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
                <ListPageLayout
                    moduleLabel="ACCOUNTS"
                    title="List of Invoice"
                    subtitle="Review invoice records, search the current list, and export invoice data."
                    modeLabel="VIEW MODE"
                    recordCount={filteredRecords.length}
                    recordLabel="invoices"
                    summaryStats={summaryStats}
                    searchText={searchText}
                    onSearchChange={setSearchText}
                    settingsSummary={settingsSummary}
                    onOpenSettings={() => setShowSettings(true)}
                    onExportCSV={handleCSVExport}
                    onExportPDF={handlePDFExport}
                    formLink="/SalesInvoice"
                    addNewLabel="Add New"
                >
                    <div className="item-list-table-container">
                        <div className="table-responsive">
                            <ReactTableWrapper
                                title="List of Invoice"
                                columns={columns}
                                data={filteredRecords}
                                onRowEdit={onEditSalesPosting}
                                hideDelete={true}
                                onCSVExport={handleCSVExport}
                                onPDFExport={handlePDFExport}
                                enableTableSearch={false}
                                isLoading={isLoading}
                                onRowPrint={onPrintSalesPosting}
                                hidePrint={false}
                                autoPinActionColumn={false}
                                compactRows={true}
                            />
                        </div>
                    </div>
                </ListPageLayout>

                <SalesListSettingsModal
                    show={showSettings}
                    allowedPlants={plants}
                    customers={customers}
                    initialSettings={salesSettings}
                    onSave={handleSettingsSave}
                    onClose={() => setShowSettings(false)}
                />
            </div>
        </div>
    );
}

export default SalesInvoiceTable;
